"use client";

import { useState, useEffect } from "react";
import type { AffiliateCheckResponse } from "@/app/api/affiliate-check/route";

const STORAGE_KEY = "affwp_validated";
const STORAGE_TTL = 30 * 60 * 1000; // 30 min session cache

type StoredResult = AffiliateCheckResponse & { ref: string; ts: number };

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function readStorage(): StoredResult | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredResult = JSON.parse(raw);
    if (Date.now() - parsed.ts > STORAGE_TTL) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(ref: string, data: AffiliateCheckResponse) {
  try {
    const entry: StoredResult = { ...data, ref, ts: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage unavailable — not critical
  }
}

export type AffiliateState = AffiliateCheckResponse & {
  ref: string | null;
  isLoading: boolean;
};

const DEFAULT_STATE: AffiliateState = {
  ref: null,
  valid: false,
  couponCode: null,
  discountRate: null,
  discountPercent: null,
  isLoading: false,
};

export function useAffiliateRef(): AffiliateState {
  const [state, setState] = useState<AffiliateState>(DEFAULT_STATE);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlRef = params.get("ref");
    const cookieRef = readCookie("affwp_ref");
    const ref = urlRef ?? cookieRef ?? null;

    if (!ref) return;

    const cached = readStorage();
    if (cached && cached.ref === ref) {
      setState({ ...cached, isLoading: false });
      return;
    }

    setState((prev) => ({ ...prev, ref, isLoading: true }));

    fetch(`/api/affiliate-check?ref=${encodeURIComponent(ref)}`, { credentials: "include" })
      .then((r) => r.json() as Promise<AffiliateCheckResponse>)
      .then((data) => {
        writeStorage(ref, data);
        if (data.valid) writeCookie("affwp_ref", ref, 30);
        setState({ ...data, ref, isLoading: false });
      })
      .catch((err) => {
        console.error("[affiliate] fetch error:", err);
        setState({ ...DEFAULT_STATE, ref, isLoading: false });
      });
  }, []);

  return state;
}
